// BUG-1714 — the two VALUE rules a ring's "First lot closes" must satisfy, and
// the wording each surface uses to say so. One owner for three doors: the
// Configure rings save, the Settings → Basics save, and the go-live gate.
//
// Pure, no clock of its own, no I/O (ADR 010 — Isomorphic Services): the server
// passes its own `now`, which is what ADR 024 — Time Model asks of every
// "is this in the past" question.
//
// Deliberately NOT the ring ORDERING rule. A ring closing before the ring above
// it is PRJ-63's signed "warn everywhere, block nothing" (BL-1574 AC 3) and stays
// a warning. These two are different classes: a close behind the clock ends live
// lots instantly (the rule Change Close Time already hard-refuses), and a close
// at or before bidding opens is the start-before-close invariant
// `validate-auction-dates.ts` already enforces for the auction itself.

import { formatRingLabel } from '@auctogon/core/rings';
import { formatEventTime } from '@auctogon/time';

/** A ring whose first lot would close at or before bidding opens. */
export interface RingClosingBeforeOpens {
  /** 1-based display position — what the operator reads as "Ring N". */
  ordinal: number;
  /** The ring's stored first-lot close, as read. */
  firstLotCloseAt: string;
}

/**
 * Does this close land at or before the auction opens?
 *
 * `<=`, matching `validateAuctionDates`' `startMs >= endMs`: a lot closing at
 * the very instant bidding opens has no bidding window at all.
 *
 * @param opts.closeAt - The ring's first-lot close (ISO or Postgres text form).
 * @param opts.startDate - The auction's opens instant.
 * @returns `false` when either side is missing or unparseable — there is no
 *   schedule to be out of order with.
 * @sideEffect none — pure.
 */
export function ringClosesAtOrBeforeOpens(opts: {
  closeAt: string | null | undefined;
  startDate: string | null | undefined;
}): boolean {
  const close = toEpochMs(opts.closeAt);
  const start = toEpochMs(opts.startDate);
  if (close == null || start == null) return false;
  return close <= start;
}

/**
 * Is this close already behind the clock?
 *
 * @param opts.closeAt - The proposed close.
 * @param opts.now - The SERVER's clock (ADR 024 — Time Model), never a device's.
 * @returns `false` for a missing or unparseable close.
 * @sideEffect none — pure.
 */
export function closeTimeIsInPast(opts: { closeAt: string | null | undefined; now: Date }): boolean {
  const close = toEpochMs(opts.closeAt);
  return close != null && close < opts.now.getTime();
}

/**
 * Every ring whose stored first-lot close lands at or before bidding opens.
 *
 * Ring 1 (index 0) is SKIPPED: its close is owned by the auction's own "First
 * lot closes", which `validateAuctionDates` already holds after the start, and
 * any value stored on that ring row is ignored by every surface that shows it
 * (`resolveRingEffectiveClose`). A blocker naming a value the operator cannot
 * see or edit would be unfixable.
 *
 * @param opts.ringFirstLotCloses - Every live ring's stored close, in canonical
 *   display order (index + 1 = the ring's ordinal). `null` for a ring that
 *   declares none — it staggers off the auction and cannot be out of order.
 * @param opts.startDate - The auction's opens instant.
 * @returns The offending rings in display order; empty when none.
 * @sideEffect none — pure.
 */
export function findRingsClosingBeforeOpens(opts: {
  ringFirstLotCloses: ReadonlyArray<string | null> | null | undefined;
  startDate: string | null | undefined;
}): RingClosingBeforeOpens[] {
  const closes = opts.ringFirstLotCloses ?? [];
  const found: RingClosingBeforeOpens[] = [];
  closes.forEach((closeAt, index) => {
    if (index === 0 || closeAt == null) return;
    if (ringClosesAtOrBeforeOpens({ closeAt, startDate: opts.startDate })) {
      found.push({ ordinal: index + 1, firstLotCloseAt: closeAt });
    }
  });
  return found;
}

/**
 * Configure rings' refusal for a close at or before bidding opens (AC 3).
 *
 * Names the Opens instant in the auction's own zone, because "before bidding
 * opens" alone sends the operator to another page to find out when that is.
 *
 * @param opts.ordinal - The ring's display position.
 * @param opts.startDate - The auction's opens instant.
 * @param opts.timeZone - The auction's IANA zone (ADR 024 — Time Model).
 * @returns e.g. "Ring 2 would close before bidding opens (Sep 28, 1:00 PM EDT)."
 * @sideEffect none — pure.
 */
export function describeRingClosesBeforeOpens(opts: {
  ordinal: number;
  startDate: string;
  timeZone: string;
}): string {
  const opens = formatEventTime(opts.startDate, opts.timeZone, { omitYear: true });
  return `${formatRingLabel(opts.ordinal)} would close before bidding opens (${opens}).`;
}

/**
 * Settings → Basics' refusal for an Opens at or after a ring's first-lot close
 * (AC 4) — names the ring and its close, which the generic "earliest lot
 * closing time" message could not.
 *
 * @param opts.ring - The offending ring.
 * @param opts.timeZone - The auction's IANA zone.
 * @returns e.g. "Start date must be before Ring 2's first lot close (Sep 1, 6:00 PM EDT)."
 * @sideEffect none — pure.
 */
export function describeStartAfterRingClose(opts: {
  ring: RingClosingBeforeOpens;
  timeZone: string;
}): string {
  const close = formatEventTime(opts.ring.firstLotCloseAt, opts.timeZone, { omitYear: true });
  return `Start date must be before ${formatRingLabel(opts.ring.ordinal)}’s first lot close (${close}).`;
}

/**
 * The go-live gate's line for the same condition (AC 5). No time in it: the
 * gate runs without the auction's zone, and naming the ring is what makes the
 * blocker actionable.
 *
 * @param ordinal - The ring's display position.
 * @returns The blocker message.
 * @sideEffect none — pure.
 */
export function describeRingClosesBeforeOpensBlocker(ordinal: number): string {
  return `${formatRingLabel(ordinal)}’s first lot closes before bidding opens — move it after the start date before the auction can go live.`;
}

function toEpochMs(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}
